import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Grid,
  Menu,
  ListItemText,
  TableSortLabel,
  Typography
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import ColumnFilter, { FilterConfig } from './ColumnFilter';
import { getCategoryOptions } from '../constants/categories';
import AuthenticatedImage from './AuthenticatedImage';

interface Item {
  id: number;
  name: string;
  quantityUnit: string;
  pricePerUnit: number | null;
  addAmountToBillInPOS: boolean;
  manufacturedInFactory: boolean;
  category: string;
  consumable: number;
  manufacturingPricePerUnit: number | null;
  avgWeightPerUnit: number | null;
  hasImage?: boolean;
  hasLabelImage?: boolean;
  // For editing/uploading - these are only used when editing
  imageData?: string;
  imageContentType?: string;
  labelImageData?: string;
  labelImageContentType?: string;
}

interface BulkPriceDraft {
  pricePerUnit: string;
  manufacturingPricePerUnit: string;
}

const Items: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [items, setItems] = useState<Item[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [showEditItemDialog, setShowEditItemDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showBulkPriceDialog, setShowBulkPriceDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [bulkPriceDrafts, setBulkPriceDrafts] = useState<Record<number, BulkPriceDraft>>({});
  const [newItem, setNewItem] = useState({
    name: '',
    quantityUnit: '',
    pricePerUnit: 0,
    addAmountToBillInPOS: true,
    manufacturedInFactory: false,
    category: '',
    manufacturingPricePerUnit: 0,
    avgWeightPerUnit: 0,
    imageData: '',
    imageContentType: '',
    labelImageData: '',
    labelImageContentType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Item>('name');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string} | null>(null);

  const columnFilterConfigs: FilterConfig[] = [
    { field: 'id', type: 'number', label: 'ID', placeholder: 'Filter by ID' },
    { field: 'name', type: 'string', label: 'Name', placeholder: 'Filter by name' },
    { field: 'category', type: 'string', label: 'Category', placeholder: 'Filter by category' },
    { field: 'quantityUnit', type: 'select', label: 'Quantity Unit', options: Array.from(new Set(items.map(item => item.quantityUnit))).map(unit => ({
      value: unit,
      label: unit
    })) },
    { field: 'pricePerUnit', type: 'number', label: 'Price Per Unit', placeholder: 'Filter by price' },
    { field: 'manufacturingPricePerUnit', type: 'number', label: 'Manufacturing Price', placeholder: 'Filter by manufacturing price' },
    { field: 'avgWeightPerUnit', type: 'number', label: 'Avg Weight', placeholder: 'Filter by weight' }
  ];

  const quantityUnitOptions = [
    { value: 'COUNT', label: 'Count' },
    { value: 'GRAM', label: 'Gram' }
  ];

  const categoryOptions = getCategoryOptions();

  useEffect(() => {
    if (token) {
      fetchItems();
    }
  }, [token]);

  const fetchItems = async () => {
    setIsLoadingItems(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory-items`, {
        headers: { Authorization: token }
      });
      setItems(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching items:', err);
      }
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false, isLabelImage: boolean = false) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        const base64Data = result.split(',')[1]; // Remove data:image/...;base64, prefix
        
        if (isEdit && editingItem) {
          if (isLabelImage) {
            setEditingItem({
              ...editingItem,
              labelImageData: base64Data,
              labelImageContentType: file.type
            });
          } else {
            setEditingItem({
              ...editingItem,
              imageData: base64Data,
              imageContentType: file.type
            });
          }
        } else {
          if (isLabelImage) {
            setNewItem({
              ...newItem,
              labelImageData: base64Data,
              labelImageContentType: file.type
            });
          } else {
            setNewItem({
              ...newItem,
              imageData: base64Data,
              imageContentType: file.type
            });
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImagePreview = async (item: Item, isLabelImage: boolean = false) => {
    const hasImage = isLabelImage ? item.hasLabelImage : item.hasImage;
    
    if (hasImage) {
      try {
        const endpoint = isLabelImage 
          ? `${API_BASE_URL}/inventory-items/${item.id}/label-image`
          : `${API_BASE_URL}/inventory-items/${item.id}/image`;
        
        const response = await axios.get(endpoint, {
          responseType: 'blob',
          headers: { Authorization: token }
        });
        
        const imageUrl = URL.createObjectURL(response.data);
        setPreviewImage({ url: imageUrl });
        setShowImagePreview(true);
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
  };

  const clearImage = (isEdit: boolean = false, isLabelImage: boolean = false) => {
    if (isEdit && editingItem) {
      if (isLabelImage) {
        setEditingItem({
          ...editingItem,
          labelImageData: '',
          labelImageContentType: ''
        });
      } else {
        setEditingItem({
          ...editingItem,
          imageData: '',
          imageContentType: ''
        });
      }
    } else {
      if (isLabelImage) {
        setNewItem({
          ...newItem,
          labelImageData: '',
          labelImageContentType: ''
        });
      } else {
        setNewItem({
          ...newItem,
          imageData: '',
          imageContentType: ''
        });
      }
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name.trim() || !newItem.category || !newItem.quantityUnit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${API_BASE_URL}/inventory-items`, newItem, {
        headers: { Authorization: token }
      });
      
      setShowAddItemDialog(false);
      setNewItem({
        name: '',
        quantityUnit: '',
        pricePerUnit: 0,
        addAmountToBillInPOS: true,
        manufacturedInFactory: false,
        category: '',
        manufacturingPricePerUnit: 0,
        avgWeightPerUnit: 0,
        imageData: '',
        imageContentType: '',
        labelImageData: '',
        labelImageContentType: ''
      });
      fetchItems();
    } catch (error: any) {
      console.error('Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditItem = async () => {
    if (!editingItem) return;
    
    setIsSubmitting(true);
    try {
      const updateData: any = {
        name: editingItem.name,
        category: editingItem.category,
        quantityUnit: editingItem.quantityUnit,
        pricePerUnit: editingItem.pricePerUnit,
        manufacturingPricePerUnit: editingItem.manufacturingPricePerUnit,
        avgWeightPerUnit: editingItem.avgWeightPerUnit,
        addAmountToBillInPOS: editingItem.addAmountToBillInPOS || false,
        manufacturedInFactory: editingItem.manufacturedInFactory || false
      };

      // Only include image data if it was actually uploaded/changed
      if (editingItem.imageData !== undefined) {
        updateData.imageData = editingItem.imageData || '';
        updateData.imageContentType = editingItem.imageContentType || '';
      }
      
      if (editingItem.labelImageData !== undefined) {
        updateData.labelImageData = editingItem.labelImageData || '';
        updateData.labelImageContentType = editingItem.labelImageContentType || '';
      }

      const response = await axios.put(
        `${API_BASE_URL}/inventory-items/${editingItem.id}`,
        updateData,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      
      setItems(prev => prev.map(item => 
        item.id === editingItem.id ? response.data : item
      ));
      setShowEditItemDialog(false);
      setEditingItem(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error updating item:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const normalizeNullableNumber = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const formatPriceDraft = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const openBulkPriceDialog = () => {
    const initialDrafts: Record<number, BulkPriceDraft> = {};
    items.forEach((item) => {
      initialDrafts[item.id] = {
        pricePerUnit: formatPriceDraft(item.pricePerUnit),
        manufacturingPricePerUnit: formatPriceDraft(item.manufacturingPricePerUnit)
      };
    });
    setBulkPriceDrafts(initialDrafts);
    setShowBulkPriceDialog(true);
  };

  const handleBulkPriceFieldChange = (
    itemId: number,
    field: keyof BulkPriceDraft,
    value: string
  ) => {
    setBulkPriceDrafts((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || { pricePerUnit: '', manufacturingPricePerUnit: '' }),
        [field]: value
      }
    }));
  };

  const getBulkPriceChanges = () => {
    return items
      .map((item) => {
        const draft = bulkPriceDrafts[item.id];
        if (!draft) return null;

        const nextPricePerUnit = normalizeNullableNumber(draft.pricePerUnit);
        const nextManufacturingPrice = normalizeNullableNumber(draft.manufacturingPricePerUnit);

        const hasChanged =
          item.pricePerUnit !== nextPricePerUnit ||
          item.manufacturingPricePerUnit !== nextManufacturingPrice;

        if (!hasChanged) return null;

        return {
          id: item.id,
          pricePerUnit: nextPricePerUnit,
          manufacturingPricePerUnit: nextManufacturingPrice
        };
      })
      .filter((change): change is { id: number; pricePerUnit: number | null; manufacturingPricePerUnit: number | null } => change !== null);
  };

  const handleSaveBulkPrices = async () => {
    const changes = getBulkPriceChanges();
    if (changes.length === 0) {
      setShowBulkPriceDialog(false);
      return;
    }

    setIsBulkSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/inventory-items/prices/bulk`,
        { items: changes },
        { headers: { Authorization: token } }
      );

      const updatedItems: Item[] = response.data;
      const updatedItemsById = new Map(updatedItems.map((item) => [item.id, item]));
      setItems((prev) => prev.map((item) => updatedItemsById.get(item.id) || item));
      setShowBulkPriceDialog(false);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error updating prices in bulk:', err);
      }
    } finally {
      setIsBulkSaving(false);
    }
  };

  const renderAddItemDialog = () => (
    <Dialog open={showAddItemDialog} onClose={() => setShowAddItemDialog(false)}>
      <DialogTitle>Add New Item</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Item Name"
          type="text"
          fullWidth
          value={newItem.name}
          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          disabled={isSubmitting}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Category</InputLabel>
          <Select
            value={newItem.category}
            onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
            label="Category"
            disabled={isSubmitting}
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel>Quantity Unit</InputLabel>
          <Select
            value={newItem.quantityUnit}
            onChange={(e) => setNewItem({ ...newItem, quantityUnit: e.target.value })}
            label="Quantity Unit"
            disabled={isSubmitting}
          >
            {quantityUnitOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          label="Price Per Unit"
          type="number"
          fullWidth
          value={newItem.pricePerUnit}
          onChange={(e) => setNewItem({ ...newItem, pricePerUnit: parseFloat(e.target.value) || 0 })}
          InputProps={{
            startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
          }}
          disabled={isSubmitting}
        />
        <TextField
          margin="dense"
          label="Manufacturing Price Per Unit"
          type="number"
          fullWidth
          value={newItem.manufacturingPricePerUnit}
          onChange={(e) => setNewItem({ ...newItem, manufacturingPricePerUnit: parseFloat(e.target.value) || 0 })}
          InputProps={{
            startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
          }}
          disabled={isSubmitting}
        />
        {newItem.quantityUnit === 'GRAM' && (
          <TextField
            margin="dense"
            label="Average Weight Per Unit"
            type="number"
            fullWidth
            value={newItem.avgWeightPerUnit}
            onChange={(e) => setNewItem({ ...newItem, avgWeightPerUnit: parseFloat(e.target.value) || 0 })}
            InputProps={{
              startAdornment: <InputAdornment position="start">g</InputAdornment>,
            }}
            disabled={isSubmitting}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={newItem.addAmountToBillInPOS}
              onChange={(e) => setNewItem({ ...newItem, addAmountToBillInPOS: e.target.checked })}
            />
          }
          label="Add Amount to Bill in POS"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={newItem.manufacturedInFactory}
              onChange={(e) => setNewItem({ ...newItem, manufacturedInFactory: e.target.checked })}
            />
          }
          label="Manufactured in Factory"
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="add-item-image-upload"
            type="file"
            onChange={(e) => handleImageUpload(e, false, false)}
          />
          <label htmlFor="add-item-image-upload">
            <Button variant="outlined" component="span" size="small">
              Upload Image
            </Button>
          </label>
          {newItem.imageData && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={`data:${newItem.imageContentType};base64,${newItem.imageData}`}
                alt="Preview"
                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
              />
              <Button size="small" color="error" onClick={() => clearImage(false, false)}>
                Remove
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2, mb: 1 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="add-item-label-image-upload"
            type="file"
            onChange={(e) => handleImageUpload(e, false, true)}
          />
          <label htmlFor="add-item-label-image-upload">
            <Button variant="outlined" component="span" size="small">
              Upload Label Image
            </Button>
          </label>
          {newItem.labelImageData && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={`data:${newItem.labelImageContentType};base64,${newItem.labelImageData}`}
                alt="Label Preview"
                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
              />
              <Button size="small" color="error" onClick={() => clearImage(false, true)}>
                Remove
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowAddItemDialog(false)} disabled={isSubmitting}>Cancel</Button>
        <Button onClick={handleAddItem} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} /> : 'Add Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditItemDialog = () => (
    <Dialog open={showEditItemDialog} onClose={() => setShowEditItemDialog(false)}>
      <DialogTitle>Edit Item</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={editingItem?.name || ''}
          onChange={(e) => setEditingItem({ ...editingItem!, name: e.target.value })}
          disabled={isSubmitting}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Category</InputLabel>
          <Select
            value={editingItem?.category || ''}
            onChange={(e) => setEditingItem({ ...editingItem!, category: e.target.value })}
            label="Category"
            disabled={isSubmitting}
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel>Quantity Unit</InputLabel>
          <Select
            value={editingItem?.quantityUnit || ''}
            onChange={(e) => setEditingItem({ ...editingItem!, quantityUnit: e.target.value })}
            label="Quantity Unit"
            disabled={true}
          >
            {quantityUnitOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          label="Price Per Unit"
          type="number"
          fullWidth
          value={editingItem?.pricePerUnit || 0}
          onChange={(e) => setEditingItem({ ...editingItem!, pricePerUnit: parseFloat(e.target.value) })}
          InputProps={{
            startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
          }}
          disabled={isSubmitting}
        />
        <TextField
          margin="dense"
          label="Manufacturing Price Per Unit"
          type="number"
          fullWidth
          value={editingItem?.manufacturingPricePerUnit || 0}
          onChange={(e) => setEditingItem({ ...editingItem!, manufacturingPricePerUnit: parseFloat(e.target.value) })}
          InputProps={{
            startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
          }}
          disabled={isSubmitting}
        />
        {editingItem?.quantityUnit === 'GRAM' && (
          <TextField
            margin="dense"
            label="Average Weight Per Unit"
            type="number"
            fullWidth
            value={editingItem?.avgWeightPerUnit || 0}
            onChange={(e) => setEditingItem({ ...editingItem!, avgWeightPerUnit: parseFloat(e.target.value) })}
            InputProps={{
              startAdornment: <InputAdornment position="start">g</InputAdornment>,
            }}
            disabled={isSubmitting}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={editingItem?.addAmountToBillInPOS || false}
              onChange={(e) => {
                if (editingItem) {
                  setEditingItem({
                    ...editingItem,
                    addAmountToBillInPOS: e.target.checked
                  });
                }
              }}
            />
          }
          label="Add Amount to Bill in POS"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={editingItem?.manufacturedInFactory || false}
              onChange={(e) => {
                if (editingItem) {
                  setEditingItem({
                    ...editingItem,
                    manufacturedInFactory: e.target.checked
                  });
                }
              }}
            />
          }
          label="Manufactured in Factory"
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="edit-item-image-upload"
            type="file"
            onChange={(e) => handleImageUpload(e, true, false)}
          />
          <label htmlFor="edit-item-image-upload">
            <Button variant="outlined" component="span" size="small">
              Upload Image
            </Button>
          </label>
          {editingItem?.imageData && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={`data:${editingItem.imageContentType};base64,${editingItem.imageData}`}
                alt="Preview"
                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
              />
              <Button size="small" color="error" onClick={() => clearImage(true, false)}>
                Remove
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2, mb: 1 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="edit-item-label-image-upload"
            type="file"
            onChange={(e) => handleImageUpload(e, true, true)}
          />
          <label htmlFor="edit-item-label-image-upload">
            <Button variant="outlined" component="span" size="small">
              Upload Label Image
            </Button>
          </label>
          {editingItem?.labelImageData && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={`data:${editingItem.labelImageContentType};base64,${editingItem.labelImageData}`}
                alt="Label Preview"
                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
              />
              <Button size="small" color="error" onClick={() => clearImage(true, true)}>
                Remove
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowEditItemDialog(false)}>Cancel</Button>
        <Button onClick={handleEditItem} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderBulkPriceDialog = () => {
    const changedCount = getBulkPriceChanges().length;

    return (
      <Dialog
        open={showBulkPriceDialog}
        onClose={() => !isBulkSaving && setShowBulkPriceDialog(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Edit Prices</DialogTitle>
        <DialogContent dividers sx={{ p: isMobile ? 1.5 : 2 }}>
          {isMobile ? (
            <Stack spacing={1.5}>
              {sortedItems.map((item) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    {item.hasImage ? (
                      <AuthenticatedImage
                        itemId={item.id}
                        isLabelImage={false}
                        alt={item.name}
                        style={{
                          width: 44,
                          height: 44,
                          objectFit: 'cover',
                          borderRadius: 6,
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          flexShrink: 0
                        }}
                      >
                        No image
                      </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                      {item.name}
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Manufacturing Price"
                        type="number"
                        value={bulkPriceDrafts[item.id]?.manufacturingPricePerUnit ?? ''}
                        onChange={(e) => handleBulkPriceFieldChange(item.id, 'manufacturingPricePerUnit', e.target.value)}
                        disabled={isBulkSaving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Price Per Unit"
                        type="number"
                        value={bulkPriceDrafts[item.id]?.pricePerUnit ?? ''}
                        onChange={(e) => handleBulkPriceFieldChange(item.id, 'pricePerUnit', e.target.value)}
                        disabled={isBulkSaving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 90 }}>Image</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Name</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Manufacturing Price</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Price Per Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.hasImage ? (
                          <AuthenticatedImage
                            itemId={item.id}
                            isLabelImage={false}
                            alt={item.name}
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                          />
                        ) : (
                          <span style={{ color: '#999', fontSize: '0.75rem' }}>No image</span>
                        )}
                      </TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={bulkPriceDrafts[item.id]?.manufacturingPricePerUnit ?? ''}
                          onChange={(e) => handleBulkPriceFieldChange(item.id, 'manufacturingPricePerUnit', e.target.value)}
                          disabled={isBulkSaving}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={bulkPriceDrafts[item.id]?.pricePerUnit ?? ''}
                          onChange={(e) => handleBulkPriceFieldChange(item.id, 'pricePerUnit', e.target.value)}
                          disabled={isBulkSaving}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 1.5 : 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
            {changedCount} item{changedCount === 1 ? '' : 's'} changed
          </Typography>
          <Button onClick={() => setShowBulkPriceDialog(false)} disabled={isBulkSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveBulkPrices}
            variant="contained"
            disabled={isBulkSaving || changedCount === 0}
          >
            {isBulkSaving ? <CircularProgress size={22} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'add':
        setShowAddItemDialog(true);
        break;
      case 'edit-prices':
        openBulkPriceDialog();
        break;
    }
  };

  const handleSort = (property: keyof Item) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const sortedItems = [...items].filter(item => {
    return Object.entries(columnFilters).every(([field, filterValue]) => {
      if (!filterValue || filterValue === '') return true;
      
      switch (field) {
        case 'id':
          return item.id.toString().includes(filterValue.toString());
        case 'name':
          return item.name.toLowerCase().includes(filterValue.toLowerCase());
        case 'category':
          return item.category.toLowerCase().includes(filterValue.toLowerCase());
        case 'quantityUnit':
          return item.quantityUnit === filterValue;
        case 'pricePerUnit':
          return item.pricePerUnit?.toString().includes(filterValue.toString()) || false;
        case 'manufacturingPricePerUnit':
          return item.manufacturingPricePerUnit?.toString().includes(filterValue.toString()) || false;
        case 'avgWeightPerUnit':
          return item.avgWeightPerUnit?.toString().includes(filterValue.toString()) || false;
        default:
          return true;
      }
    });
  }).sort((a, b) => {
    let aValue: any = a[orderBy];
    let bValue: any = b[orderBy];
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {(hasPermission('add-item') || hasPermission('edit-item')) && (
            <>
              <Button
                variant="outlined"
                onClick={handleActionsMenuOpen}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={handleActionsMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: '70vh',
                    width: '100%',
                    maxWidth: '280px'
                  }
                }}
              >
                {hasPermission('add-item') && (
                  <MenuItem onClick={() => handleActionSelect('add')}>
                    <ListItemText primary="Add Item" />
                  </MenuItem>
                )}
                {hasPermission('edit-item') && (
                  <MenuItem onClick={() => handleActionSelect('edit-prices')}>
                    <ListItemText primary="Edit Prices" />
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, maxWidth: '600px' }}>
              {/* Search can be added here if needed */}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
        {hasPermission('add-item') && (
                <Button 
                  variant="contained" 
                  onClick={() => setShowAddItemDialog(true)}
                  size="small"
                >
            Add Item
          </Button>
        )}
              {hasPermission('edit-item') && (
                <Button
                  variant="outlined"
                  onClick={openBulkPriceDialog}
                  size="small"
                >
                  Edit Prices
                </Button>
              )}
      </Box>
          </Box>
        </Box>
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          '& .MuiTableCell-root': {
            padding: isMobile ? '6px 8px' : '8px 16px',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[0]}
                    value={columnFilters.id}
                    onChange={(value) => handleColumnFilterChange('id', value)}
                    onClear={() => handleColumnFilterClear('id')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '80px' }}>Image</TableCell>
              <TableCell sx={{ minWidth: '80px' }}>Label Image</TableCell>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[1]}
                    value={columnFilters.name}
                    onChange={(value) => handleColumnFilterChange('name', value)}
                    onClear={() => handleColumnFilterClear('name')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'category'}
                    direction={orderBy === 'category' ? order : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Category
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[2]}
                    value={columnFilters.category}
                    onChange={(value) => handleColumnFilterChange('category', value)}
                    onClear={() => handleColumnFilterClear('category')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'quantityUnit'}
                    direction={orderBy === 'quantityUnit' ? order : 'asc'}
                    onClick={() => handleSort('quantityUnit')}
                  >
                    Quantity Unit
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[3]}
                    value={columnFilters.quantityUnit}
                    onChange={(value) => handleColumnFilterChange('quantityUnit', value)}
                    onClear={() => handleColumnFilterClear('quantityUnit')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={orderBy === 'pricePerUnit'}
                    direction={orderBy === 'pricePerUnit' ? order : 'asc'}
                    onClick={() => handleSort('pricePerUnit')}
                  >
                    Price Per Unit
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[4]}
                    value={columnFilters.pricePerUnit}
                    onChange={(value) => handleColumnFilterChange('pricePerUnit', value)}
                    onClear={() => handleColumnFilterClear('pricePerUnit')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={orderBy === 'manufacturingPricePerUnit'}
                    direction={orderBy === 'manufacturingPricePerUnit' ? order : 'asc'}
                    onClick={() => handleSort('manufacturingPricePerUnit')}
                  >
                    Manufacturing Price
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[5]}
                    value={columnFilters.manufacturingPricePerUnit}
                    onChange={(value) => handleColumnFilterChange('manufacturingPricePerUnit', value)}
                    onClear={() => handleColumnFilterClear('manufacturingPricePerUnit')}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TableSortLabel
                    active={orderBy === 'avgWeightPerUnit'}
                    direction={orderBy === 'avgWeightPerUnit' ? order : 'asc'}
                    onClick={() => handleSort('avgWeightPerUnit')}
                  >
                    Avg Weight (g)
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[6]}
                    value={columnFilters.avgWeightPerUnit}
                    onChange={(value) => handleColumnFilterChange('avgWeightPerUnit', value)}
                    onClear={() => handleColumnFilterClear('avgWeightPerUnit')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>Add to Bill</TableCell>
              <TableCell sx={{ minWidth: '100px' }}>Manufactured</TableCell>
              {hasPermission('edit-item') && <TableCell sx={{ minWidth: '120px' }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.id}</TableCell>
                <TableCell>
                  {item.hasImage ? (
                    <AuthenticatedImage
                      itemId={item.id}
                      isLabelImage={false}
                      alt={item.name}
                      style={{ 
                        width: 40, 
                        height: 40, 
                        objectFit: 'cover', 
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleImagePreview(item, false)}
                    />
                  ) : (
                    <span style={{ color: '#999', fontSize: '0.75rem' }}>No image</span>
                  )}
                </TableCell>
                <TableCell>
                  {item.hasLabelImage ? (
                    <AuthenticatedImage
                      itemId={item.id}
                      isLabelImage={true}
                      alt={`${item.name} label`}
                      style={{ 
                        width: 40, 
                        height: 40, 
                        objectFit: 'cover', 
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleImagePreview(item, true)}
                    />
                  ) : (
                    <span style={{ color: '#999', fontSize: '0.75rem' }}>No label</span>
                  )}
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.quantityUnit}</TableCell>
                <TableCell align="right">PKR {item.pricePerUnit?.toFixed(2) || 'N/A'}</TableCell>
                <TableCell align="right">PKR {item.manufacturingPricePerUnit?.toFixed(2) || 'N/A'}</TableCell>
                <TableCell align="right">{item.quantityUnit === 'GRAM' ? (item.avgWeightPerUnit?.toFixed(2) || 'N/A') : '-'}</TableCell>
                <TableCell>{item.addAmountToBillInPOS ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item.manufacturedInFactory ? 'Yes' : 'No'}</TableCell>
                {hasPermission('edit-item') && (
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        setEditingItem(item);
                        setShowEditItemDialog(true);
                        
                        // Load current images if they exist
                        const token = localStorage.getItem('token');
                        if (item.hasImage) {
                          try {
                            const response = await axios.get(
                              `${API_BASE_URL}/inventory-items/${item.id}/image`,
                              { responseType: 'blob', headers: { Authorization: token } }
                            );
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const base64data = reader.result as string;
                              const base64 = base64data.split(',')[1];
                              const contentType = response.headers['content-type'];
                              setEditingItem(prev => prev ? {
                                ...prev,
                                imageData: base64,
                                imageContentType: typeof contentType === 'string' ? contentType : 'image/jpeg'
                              } : null);
                            };
                            reader.readAsDataURL(response.data);
                          } catch (error) {
                            console.error('Error loading image:', error);
                          }
                        }
                        if (item.hasLabelImage) {
                          try {
                            const response = await axios.get(
                              `${API_BASE_URL}/inventory-items/${item.id}/label-image`,
                              { responseType: 'blob', headers: { Authorization: token } }
                            );
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const base64data = reader.result as string;
                              const base64 = base64data.split(',')[1];
                              const contentType = response.headers['content-type'];
                              setEditingItem(prev => prev ? {
                                ...prev,
                                labelImageData: base64,
                                labelImageContentType: typeof contentType === 'string' ? contentType : 'image/jpeg'
                              } : null);
                            };
                            reader.readAsDataURL(response.data);
                          } catch (error) {
                            console.error('Error loading label image:', error);
                          }
                        }
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {renderAddItemDialog()}
      {renderEditItemDialog()}
      {renderBulkPriceDialog()}
      
      {/* Image Preview Dialog */}
      <Dialog 
        open={showImagePreview} 
        onClose={() => {
          if (previewImage?.url) {
            URL.revokeObjectURL(previewImage.url);
          }
          setShowImagePreview(false);
          setPreviewImage(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <img
                src={previewImage.url}
                alt="Preview"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh', 
                  objectFit: 'contain' 
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (previewImage?.url) {
              URL.revokeObjectURL(previewImage.url);
            }
            setShowImagePreview(false);
            setPreviewImage(null);
          }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Items;
